"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Phone, CircleCheck as CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, House, LoaderCircle, PhoneCall, ShieldCheck, CircleCheck } from "lucide-react";

/* ===================== GNCI booking form (shared code) =====================
   Source of truth: project doc claude/booking-system.md. The same block is pasted into
   KitchenRemodeling/sections/StartProject.tsx and BathroomRemodeling/sections/Conversion.tsx
   (the site builder cannot create shared files). Keep both copies identical.
   Backend: Google Apps Script web app owned by contact@gn-construction.com.
============================================================================= */
const GNCI_BOOKING_API = "https://script.google.com/macros/s/AKfycbzoy3RRRYLe-C5r3kKrRxEi94J0emcXbWSShcxv6n5rD6Nz3p1Yycg3Y-w4IOTrFm4/exec";
const GNCI_MAPS_KEY = "AIzaSyCuC4Z4-1vXNqle8YNIgmso5j0jCVPRrBQ";
const GNCI_PHONE = "(909) 906-3355";
const GNCI_TEL = "tel:+19099063355";
const GNCI_ATTR_KEYS = ["gclid", "gbraid", "wbraid", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "campaign", "ad_group", "ad", "keyword", "search_term"];
type GnciService = "kitchen" | "bathroom" | "adu" | "additions" | "roofing" | "painting";
type GnciType = "onsite" | "call";
type GnciSlot = {
  start: string;
  label: string;
};
type GnciDay = {
  date: string;
  label: string;
  slots: GnciSlot[];
};
type GnciAddress = {
  street: string;
  unit: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  formatted: string;
  place_id: string;
  lat: string;
  lng: string;
};

/* One entry per service: the page headline, the line under it, and the name shown to the customer. */
const GNCI_SERVICES: Record<string, {
  label: string;
  title: string;
  intro: string;
}> = {
  kitchen: {
    label: "Kitchen Remodeling",
    title: "Kitchen Remodeling in the Inland Empire, Orange County & Los Angeles",
    intro: "Complete kitchen remodels planned, permitted and built by a licensed California general contractor. Pick a time below for your free estimate."
  },
  bathroom: {
    label: "Bathroom Remodeling",
    title: "Bathroom Remodeling in the Inland Empire, Orange County & Los Angeles",
    intro: "Full bathroom remodels with permits, proper waterproofing and a clear schedule, built by a licensed California general contractor. Pick a time below for your free estimate."
  },
  adu: {
    label: "ADU Construction",
    title: "ADU Construction in the Inland Empire, Orange County & Los Angeles",
    intro: "Detached ADUs, junior ADUs and garage conversions, from plans and permits through final inspection. Pick a time below for your free estimate."
  },
  additions: {
    label: "Home and Room Additions",
    title: "Home and Room Additions in the Inland Empire, Orange County & Los Angeles",
    intro: "Bedroom, kitchen, family room and primary suite additions, engineered, permitted and built by one licensed team. Pick a time below for your free estimate."
  },
  roofing: {
    label: "Roofing and Re-Roofing",
    title: "Roofing and Re-Roofing in the Inland Empire, Orange County & Los Angeles",
    intro: "Full roof replacements, re-roofs and underlayment work by a licensed California general contractor. Pick a time below for your free estimate."
  },
  painting: {
    label: "Interior and Exterior Painting",
    title: "Interior and Exterior Painting in the Inland Empire, Orange County & Los Angeles",
    intro: "Interior and exterior painting with proper preparation, done by the same licensed team that handles our remodels. Pick a time below for your free estimate."
  }
};
const gnciEmptyAddress: GnciAddress = {
  street: "",
  unit: "",
  city: "",
  county: "",
  state: "CA",
  zip: "",
  formatted: "",
  place_id: "",
  lat: "",
  lng: ""
};
function gnciUid(): string {
  try {
    const c = (window as any).crypto;
    if (c && c.randomUUID) return c.randomUUID();
  } catch (e) {}
  return "gnci-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12);
}
function gnciCaptureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    // A new ad or campaign click replaces the whole attribution set (no mixing of old and new tags).
    if (GNCI_ATTR_KEYS.some(k => params.get(k))) {
      GNCI_ATTR_KEYS.forEach(k => sessionStorage.removeItem("gnc_" + k));
      sessionStorage.setItem("gnc_landing_page", window.location.href);
      if (document.referrer) sessionStorage.setItem("gnc_referrer", document.referrer);else sessionStorage.removeItem("gnc_referrer");
    }
    GNCI_ATTR_KEYS.forEach(k => {
      const v = params.get(k);
      if (v) sessionStorage.setItem("gnc_" + k, v);
    });
    if (!sessionStorage.getItem("gnc_landing_page")) sessionStorage.setItem("gnc_landing_page", window.location.href);
    if (!sessionStorage.getItem("gnc_referrer") && document.referrer) sessionStorage.setItem("gnc_referrer", document.referrer);
  } catch (e) {}
}
function gnciReadAttribution(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    GNCI_ATTR_KEYS.concat(["landing_page", "referrer"]).forEach(k => {
      const v = sessionStorage.getItem("gnc_" + k);
      if (v) out[k] = v;
    });
  } catch (e) {}
  return out;
}
function gnciLoadPlaces(): Promise<any> {
  const w = window as any;
  if (!GNCI_MAPS_KEY) return Promise.resolve(null);
  if (w.__gncPlaces) return w.__gncPlaces;
  w.__gncPlaces = new Promise(resolve => {
    const done = () => {
      try {
        w.google.maps.importLibrary("places").then(resolve).catch(() => resolve(null));
      } catch (e) {
        resolve(null);
      }
    };
    if (w.google && w.google.maps && w.google.maps.importLibrary) {
      done();
      return;
    }
    w.__gncMapsReady = done;
    const s = document.createElement("script");
    s.src = "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(GNCI_MAPS_KEY) + "&loading=async&v=weekly&callback=__gncMapsReady";
    s.async = true;
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return w.__gncPlaces;
}

/* Open times are fetched once, as soon as the page loads (both appointment types in one request),
   so they are ready by the time a visitor picks an appointment type. */
async function gnciFetchJson(url: string, ms: number): Promise<any> {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => {
    try {
      ctl && ctl.abort();
    } catch (e) {}
  }, ms);
  try {
    const r = await fetch(url, ctl ? {
      signal: ctl.signal
    } : undefined);
    return JSON.parse(await r.text());
  } finally {
    clearTimeout(timer);
  }
}
function gnciPreloadSlots(force = false): Promise<any> {
  const w = window as any;
  if (w.__gnciSlots && !force) return w.__gnciSlots;
  // Google's script address sometimes stalls for many seconds before answering. Send a backup
  // request at 2.5 s, 5 s and 9 s if nothing has come back yet, and use whichever answers first.
  const p = new Promise<any>((resolve, reject) => {
    let done = false;
    let pending = 0;
    let launched = 0;
    const timers: any[] = [];
    const stop = () => {
      done = true;
      timers.forEach(t => clearTimeout(t));
    };
    const check = () => {
      if (done || pending > 0) return;
      if (launched < 4) launch();else {
        stop();
        reject(new Error("slots"));
      }
    };
    const launch = () => {
      if (done || launched >= 4) return;
      launched++;
      pending++;
      gnciFetchJson(GNCI_BOOKING_API + "?action=slots_all&n=" + launched + "-" + Date.now(), 25000).then(j => {
        pending--;
        if (!done && j && j.ok && j.onsite && j.onsite.ok && j.call && j.call.ok) {
          stop();
          resolve(j);
        } else check();
      }).catch(() => {
        pending--;
        check();
      });
    };
    launch();
    [2500, 5000, 9000].forEach(ms => timers.push(setTimeout(launch, ms)));
  });
  w.__gnciSlots = p.then(j => {
    try {
      localStorage.setItem("gnci_slots_cache", JSON.stringify({
        at: Date.now(),
        j
      }));
    } catch (e) {}
    return j;
  }, e => {
    w.__gnciSlots = null;
    throw e;
  });
  return w.__gnciSlots;
}

/* Open times saved in this browser within the last 10 minutes (repeat visitors see them instantly;
   every booking is rechecked against the live calendar). */
function gnciCachedSlots(): any {
  try {
    const raw = localStorage.getItem("gnci_slots_cache");
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || !o.j || Date.now() - o.at > 10 * 60000) return null;
    return o.j;
  } catch (e) {
    return null;
  }
}
function GnciReq() {
  return <span className="text-destructive" aria-hidden="true"> *</span>;
}
function GnciAddressField({
  value,
  onChange,
  errors
}: {
  value: GnciAddress;
  onChange: (a: GnciAddress) => void;
  errors?: Record<string, string>;
}) {
  const ec = (k: string) => errors && errors[k] ? " border-destructive ring-1 ring-destructive" : "";
  const [items, setItems] = React.useState<any[]>([]);
  const [open, setOpen] = React.useState(false);
  const [lookupOk, setLookupOk] = React.useState(true);
  const libRef = React.useRef<any>(null);
  const tokenRef = React.useRef<any>(null);
  const timerRef = React.useRef<any>(null);
  const reqRef = React.useRef(0);
  const lookup = (text: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!text || text.trim().length < 4) {
      setItems([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      const lib = libRef.current || (libRef.current = await gnciLoadPlaces());
      if (!lib || !lib.AutocompleteSuggestion) {
        setLookupOk(false);
        return;
      }
      if (!tokenRef.current) tokenRef.current = new lib.AutocompleteSessionToken();
      const my = ++reqRef.current;
      try {
        const res = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: text,
          sessionToken: tokenRef.current,
          includedRegionCodes: ["us"],
          locationBias: {
            center: {
              lat: 34,
              lng: -117.6
            },
            radius: 50000
          }
        });
        if (my !== reqRef.current) return;
        const list = (res && res.suggestions || []).filter((s: any) => s.placePrediction).slice(0, 5);
        setItems(list);
        setOpen(list.length > 0);
      } catch (e) {
        setItems([]);
        setOpen(false);
      }
    }, 250);
  };
  const pick = async (sug: any) => {
    const pred = sug.placePrediction;
    setOpen(false);
    setItems([]);
    try {
      const place = pred.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "location", "id"]
      });
      const comps: any[] = place.addressComponents || [];
      const get = (type: string, short = false) => {
        const c = comps.find((x: any) => (x.types || []).includes(type));
        return c ? short ? c.shortText : c.longText : "";
      };
      const street = [get("street_number"), get("route", true)].filter(Boolean).join(" ");
      const loc = place.location;
      onChange({
        street,
        unit: get("subpremise") || value.unit,
        city: get("locality") || get("sublocality") || get("postal_town") || get("administrative_area_level_3"),
        county: get("administrative_area_level_2"),
        state: get("administrative_area_level_1", true) || "CA",
        zip: get("postal_code"),
        formatted: place.formattedAddress || "",
        place_id: place.id || "",
        lat: loc ? String(loc.lat()) : "",
        lng: loc ? String(loc.lng()) : ""
      });
    } catch (e) {
      setLookupOk(false);
    }
    tokenRef.current = null;
  };
  const manual = (patch: Partial<GnciAddress>) => onChange({
    ...value,
    ...patch,
    formatted: "",
    place_id: "",
    lat: "",
    lng: ""
  });
  return <div className="space-y-3">
      <div className="relative">
        <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="gnci-street">Project address<GnciReq /></label>
        <input id="gnci-street" autoComplete="off" required aria-required="true" aria-invalid={!!(errors && errors.street)} className={"w-full h-12 rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary" + ec("street")} placeholder="Start typing the street address" value={value.street} onChange={e => {
        manual({
          street: e.target.value
        });
        lookup(e.target.value);
      }} onBlur={() => setTimeout(() => setOpen(false), 200)} />
        {open && <ul className="absolute z-30 mt-1 w-full rounded-md border border-border bg-background shadow-lg overflow-hidden" role="listbox">
            {items.map((s, i) => <li key={i}>
                <button type="button" className="w-full text-left px-3 py-3 text-sm hover:bg-muted" onMouseDown={e => {
            e.preventDefault();
            pick(s);
          }}>
                  {String(s.placePrediction.text && s.placePrediction.text.text ? s.placePrediction.text.text : s.placePrediction.text)}
                </button>
              </li>)}
          </ul>}
        {errors && errors.street && <p className="mt-1 text-sm text-destructive">{errors.street}</p>}
        {!lookupOk && <p className="mt-1 text-xs text-muted-foreground">Address suggestions are unavailable right now. Please type the address below.</p>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <div className="col-span-2 sm:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="gnci-unit">Unit (optional)</label>
          <input id="gnci-unit" className="w-full h-12 rounded-md border border-input bg-background px-3 text-base" value={value.unit} onChange={e => onChange({
          ...value,
          unit: e.target.value
        })} />
        </div>
        <div className="col-span-1 sm:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="gnci-city">City<GnciReq /></label>
          <input id="gnci-city" required aria-required="true" autoComplete="address-level2" className={"w-full h-12 rounded-md border border-input bg-background px-3 text-base" + ec("city")} value={value.city} onChange={e => manual({
          city: e.target.value
        })} />
        </div>
        <div className="col-span-1 sm:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="gnci-zip">ZIP<GnciReq /></label>
          <input id="gnci-zip" required aria-required="true" inputMode="numeric" autoComplete="postal-code" maxLength={5} className={"w-full h-12 rounded-md border border-input bg-background px-3 text-base" + ec("zip")} value={value.zip} onChange={e => manual({
          zip: e.target.value.replace(/\D/g, "").slice(0, 5)
        })} />
        </div>
      </div>
      {errors && (errors.city || errors.zip) && <p className="text-sm text-destructive">{errors.city || errors.zip}</p>}
      {value.place_id ? <p className="text-xs text-muted-foreground">Address found: {value.formatted}</p> : null}
    </div>;
}
function GnciBooking({
  service,
  anchor = "schedule",
  heading = "h1"
}: {
  service: GnciService;
  anchor?: string;
  heading?: "h1" | "h2";
}) {
  const info = GNCI_SERVICES[service] || GNCI_SERVICES.kitchen;
  const serviceLabel = info.label;
  const pageTitle = info.title;
  const intro = info.intro;
  const Heading: any = heading;
  const [type, setType] = React.useState<GnciType | null>(null);
  const [days, setDays] = React.useState<GnciDay[] | null>(null);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [slotError, setSlotError] = React.useState("");
  const [dayIdx, setDayIdx] = React.useState(0);
  const [start, setStart] = React.useState<GnciSlot | null>(null);
  const [first, setFirst] = React.useState("");
  const [last, setLast] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [address, setAddress] = React.useState<GnciAddress>(gnciEmptyAddress);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [notes, setNotes] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [hp, setHp] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [done, setDone] = React.useState<any>(null);
  const keyRef = React.useRef<string>("");
  const shownAt = React.useRef<number>(Date.now());
  React.useEffect(() => {
    gnciCaptureAttribution();
    keyRef.current = gnciUid();
  }, []);
  const loadSlots = React.useCallback(async (t: GnciType, refresh = false, skipStart = "") => {
    setSlotError("");
    setStart(null);
    const w = window as any;
    // Show saved results instantly when the page-load request has already finished.
    const ready = !refresh && (w.__gnciSlotsData || gnciCachedSlots());
    if (ready) {
      setDays(ready[t].days || []);
      setDayIdx(0);
      setLoadingSlots(false);
      return;
    }
    setDays(null);
    setLoadingSlots(true);
    try {
      let j: any;
      try {
        j = await gnciPreloadSlots(refresh);
      } catch (first) {
        j = await gnciPreloadSlots(true);
      } // one fresh try if the page-load request failed
      w.__gnciSlotsData = j;
      const list: GnciDay[] = (j[t].days || []).map((d: GnciDay) => ({
        ...d,
        slots: d.slots.filter(x => x.start !== skipStart)
      })).filter((d: GnciDay) => d.slots.length > 0);
      setDays(list);
      setDayIdx(0);
    } catch (e) {
      setSlotError("We could not load open times. Please call " + GNCI_PHONE + " and we will schedule you.");
    }
    setLoadingSlots(false);
  }, []);
  React.useEffect(() => {
    gnciPreloadSlots().then(j => {
      (window as any).__gnciSlotsData = j;
    }).catch(() => {});
  }, []);
  const chooseType = (t: GnciType) => {
    setType(t);
    setError("");
    loadSlots(t);
    try {
      (window as any).gtag && (window as any).gtag("event", "booking_start", {
        service: service,
        appointment_type: t
      });
    } catch (e) {}
  };

  /* Clear validation messages as soon as the customer fixes the field, so a stale
     "required fields" banner cannot sit on a form that is already complete. */
  React.useEffect(() => {
    const keys = Object.keys(fieldErrors);
    if (!keys.length) return;
    const digits = phone.replace(/\D/g, "");
    const ok: Record<string, boolean> = {
      first: !!first.trim(),
      last: !!last.trim(),
      phone: digits.length === 10 || digits.length === 11 && digits[0] === "1",
      email: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim()),
      street: !!address.street.trim() && /\d/.test(address.street),
      city: !!address.city.trim(),
      zip: address.zip.length === 5,
      consent: consent
    };
    const next: Record<string, string> = {};
    keys.forEach(k => {
      if (!ok[k]) next[k] = fieldErrors[k];
    });
    const remaining = Object.keys(next).length;
    if (remaining !== keys.length) setFieldErrors(next);
    if (!remaining) setError("");
  }, [first, last, phone, email, address, consent, fieldErrors]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !type || !start) return;
    setError("");
    const digits = phone.replace(/\D/g, "");
    const fe: Record<string, string> = {};
    if (!first.trim()) fe.first = "First name is required.";
    if (!last.trim()) fe.last = "Last name is required.";
    if (!(digits.length === 10 || digits.length === 11 && digits[0] === "1")) fe.phone = "Enter a 10-digit mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) fe.email = "Enter a valid email address.";
    if (!address.street.trim()) fe.street = "Project address is required.";else if (!/\d/.test(address.street)) fe.street = "Include the street number.";
    if (!address.city.trim()) fe.city = "City is required.";
    if (address.zip.length !== 5) fe.zip = "Enter the 5-digit ZIP code.";
    if (!consent) fe.consent = "Please check the box to agree to be contacted about this inquiry.";
    setFieldErrors(fe);
    const order = ["first", "last", "phone", "email", "street", "city", "zip", "consent"];
    const firstBad = order.find(k => fe[k]);
    if (firstBad) {
      setError("Please complete the required fields marked with *.");
      const el = document.getElementById("gnci-" + firstBad);
      if (el) {
        try {
          el.focus();
          el.scrollIntoView({
            block: "center"
          });
        } catch (x) {}
      }
      return;
    }
    setSubmitting(true);
    const attribution = gnciReadAttribution();
    const payload = {
      action: "book",
      form_id: "gnci_booking_" + service,
      service,
      appointment_type: type,
      start: start.start,
      first_name: first.trim(),
      last_name: last.trim(),
      phone,
      email: email.trim(),
      address,
      city_zip: "",
      notes: notes.trim(),
      consent: true,
      consent_at: new Date().toISOString(),
      idempotency_key: keyRef.current,
      page_url: window.location.href,
      attribution,
      company_website: hp,
      elapsed_ms: Date.now() - shownAt.current
    };
    const send = async () => {
      const r = await fetch(GNCI_BOOKING_API, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=utf-8"
        },
        body: JSON.stringify(payload)
      });
      const txt = await r.text();
      try {
        return JSON.parse(txt);
      } catch (x) {
        return {
          ok: false,
          error: "bad_response"
        };
      }
    };
    try {
      let j = await send();
      // Rare Google hiccup: retry once with the same booking key (safe: the server never books a key twice).
      if (!j.ok && (j.error === "unknown_action" || j.error === "bad_response" || j.error === "busy")) {
        await new Promise(res => setTimeout(res, 1500));
        j = await send();
      }
      if (j.ok) {
        setDone(j);
        try {
          const w = window as any;
          const firedKey = "gnci_conv_" + keyRef.current;
          if (typeof w.gtag === "function" && !sessionStorage.getItem(firedKey)) {
            sessionStorage.setItem(firedKey, "1");
            w.gtag("event", "generate_lead", {
              form_id: payload.form_id,
              service: serviceLabel,
              appointment_type: type
            });
            const d = digits.length === 10 ? "+1" + digits : "+" + digits;
            w.gtag("set", "user_data", {
              email: payload.email.toLowerCase(),
              phone_number: d
            });
            // Primary conversion (fires once per booking): Website Lead Form - Start Your Project
            w.gtag("event", "conversion", {
              send_to: "AW-18356619749/M9UWCNePtvocEOWTj7FE",
              transaction_id: keyRef.current
            });
          }
        } catch (x) {}
        const toConfirmation = () => {
          const el = document.getElementById(anchor);
          if (el) window.scrollTo({
            top: el.getBoundingClientRect().top + window.scrollY - 90,
            behavior: "auto"
          });
        };
        setTimeout(toConfirmation, 100);
        setTimeout(toConfirmation, 700);
      } else if (j.error === "slot_taken") {
        setError(j.message || "That time was just taken. Please choose another time.");
        loadSlots(type, true, start.start);
      } else {
        setError(j.message || "We could not complete the booking. Please call " + GNCI_PHONE + ".");
      }
    } catch (err) {
      setError("We could not reach our scheduling system. Please try again, or call " + GNCI_PHONE + ".");
    }
    setSubmitting(false);
  };
  const inputCls = "w-full h-12 rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-primary";
  const day = days && days[dayIdx];
  return <section id={anchor} data-nav="light" className="bg-background text-foreground pt-8 pb-14 md:pt-12 md:pb-20 scroll-mt-24">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="text-center mb-6 md:mb-8">
          <Heading className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">{pageTitle}</Heading>
          <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">{intro}</p>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-medium text-foreground">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-4 text-primary" />Licensed, Bonded & Insured</span>
            <span className="text-muted-foreground">|</span><span>CSLB #1134435</span>
            <span className="text-muted-foreground">|</span><a href={GNCI_TEL} className="text-primary underline">{GNCI_PHONE}</a>
          </p>
        </div>

        {done ? <div className="rounded-xl border border-border bg-card p-6 md:p-10 shadow-sm" role="status" aria-live="polite">
            <div className="flex items-center gap-3 text-primary">
              <CircleCheck className="size-7" />
              <h3 className="text-2xl font-semibold text-foreground">
                {done.type === "onsite" ? "Your free onsite project consultation is confirmed." : "Your remodeling intake call is confirmed."}
              </h3>
            </div>
            <dl className="mt-6 grid gap-3 text-base">
              <div><dt className="inline font-semibold">Service: </dt><dd className="inline">{done.service}</dd></div>
              <div><dt className="inline font-semibold">Date: </dt><dd className="inline">{done.date}</dd></div>
              <div><dt className="inline font-semibold">{done.type === "onsite" ? "Arrival Window: " : "Call Time: "}</dt><dd className="inline">{done.time_text}{done.type === "call" ? " (Pacific)" : ""}</dd></div>
              {done.type === "onsite" && <div><dt className="inline font-semibold">Project Address: </dt><dd className="inline">{done.address}</dd></div>}
            </dl>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              {done.type === "onsite" ? "Your GNCI Project Consultant will meet you at the property to learn about your remodeling plans and review the project area." : "A member of our team will call you at the number you provided to learn about your project and answer your questions."}
              {" "}We sent a confirmation to your email. If you need to make a change, contact Good Neighbor Construction at{" "}
              <a className="font-semibold text-primary underline" href={GNCI_TEL}>{GNCI_PHONE}</a>.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">What happens next: we review your request before the appointment. A final construction price is not provided at this first appointment; if your project needs a written proposal, we will explain what we need to prepare it.</p>
          </div> : <form onSubmit={submit} className="rounded-xl border border-border bg-card p-5 md:p-8 shadow-sm space-y-8" noValidate>
            <p className="text-xl md:text-2xl font-semibold -mb-2">Schedule Your Free Estimate</p>
            {/* Step 1: appointment type */}
            <fieldset style={{
          border: 0,
          padding: 0,
          margin: 0
        }} className="min-w-0">
              <legend className="text-lg font-semibold mb-3">1. Choose your appointment</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {([{
              id: "onsite",
              title: "Free Onsite Project Consultation",
              desc: "A GNCI Project Consultant visits your home to learn about your plans and review the project area.",
              Icon: House
            }, {
              id: "call",
              title: "Remodeling Intake Call",
              desc: "A short phone call to talk through your project and next steps.",
              Icon: PhoneCall
            }] as const).map(o => <button key={o.id} type="button" onClick={() => chooseType(o.id)} aria-pressed={type === o.id} className={"text-left rounded-lg border p-4 min-h-[112px] transition-colors " + (type === o.id ? "border-primary ring-2 ring-primary bg-primary/5" : "border-border hover:border-primary/60")}>
                    <span className="flex items-center gap-2 font-semibold"><o.Icon className="size-5 text-primary" />{o.title}</span>
                    <span className="mt-1.5 block text-sm text-muted-foreground">{o.desc}</span>
                  </button>)}
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                Prefer to talk now? Call <a href={GNCI_TEL} className="font-semibold text-primary underline">{GNCI_PHONE}</a> (Mon to Fri 8 AM to 6 PM, Sat 9 AM to 3 PM).
              </p>
            </fieldset>

            {/* Step 2: date and time */}
            {type && <fieldset style={{
          border: 0,
          padding: 0,
          margin: 0
        }} className="min-w-0">
                <legend className="text-lg font-semibold mb-3">2. Pick a date and time</legend>
                {loadingSlots && <p className="flex items-center gap-2 text-muted-foreground" role="status"><LoaderCircle className="size-4 animate-spin" /> Loading open times...</p>}
                {slotError && <p className="text-destructive">{slotError}</p>}
                {days && days.length === 0 && <p className="text-muted-foreground">No open times are available online right now. Please call <a href={GNCI_TEL} className="font-semibold text-primary underline">{GNCI_PHONE}</a>.</p>}
                {days && days.length > 0 && <div className="space-y-4">
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" role="tablist" aria-label="Available dates">
                      {days.map((d, i) => <button key={d.date} type="button" role="tab" aria-selected={i === dayIdx} onClick={() => {
                setDayIdx(i);
                setStart(null);
              }} className={"shrink-0 rounded-md border px-3 py-2 text-sm min-h-[44px] " + (i === dayIdx ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/60")}>
                          <CalendarDays className="inline size-4 mr-1 -mt-0.5" />{d.label}
                        </button>)}
                    </div>
                    {day && <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {day.slots.map(s => <button key={s.start} type="button" onClick={() => setStart(s)} aria-pressed={!!start && start.start === s.start} className={"rounded-md border px-3 py-3 text-sm min-h-[44px] " + (start && start.start === s.start ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/60")}>
                            {type === "onsite" ? "Arrive " + s.label : s.label}
                          </button>)}
                      </div>}
                    {start && <p className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="size-4" />
                        {type === "onsite" ? "Your GNCI Project Consultant will arrive between " + start.label + " and " + new Date(new Date(start.start).getTime() + 30 * 60000).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                timeZone: "America/Los_Angeles"
              }) + "." : "We will call you at " + start.label + " (Pacific)."}
                      </p>}
                  </div>}
              </fieldset>}

            {/* Step 3: details */}
            {type && start && <fieldset style={{
          border: 0,
          padding: 0,
          margin: 0
        }} className="min-w-0 space-y-4">
                <legend className="text-lg font-semibold mb-1">3. Your details</legend>
                <p className="text-sm text-muted-foreground">Fields marked <span className="text-destructive">*</span> are required.</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="gnci-first">First name<GnciReq /></label>
                    <input id="gnci-first" autoComplete="given-name" required aria-required="true" aria-invalid={!!fieldErrors.first} className={inputCls + (fieldErrors.first ? " border-destructive ring-1 ring-destructive" : "")} value={first} onChange={e => {
                setFirst(e.target.value);
                if (fieldErrors.first) setFieldErrors({
                  ...fieldErrors,
                  first: ""
                });
              }} />
                    {fieldErrors.first && <p className="mt-1 text-sm text-destructive">{fieldErrors.first}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="gnci-last">Last name<GnciReq /></label>
                    <input id="gnci-last" autoComplete="family-name" required aria-required="true" aria-invalid={!!fieldErrors.last} className={inputCls + (fieldErrors.last ? " border-destructive ring-1 ring-destructive" : "")} value={last} onChange={e => {
                setLast(e.target.value);
                if (fieldErrors.last) setFieldErrors({
                  ...fieldErrors,
                  last: ""
                });
              }} />
                    {fieldErrors.last && <p className="mt-1 text-sm text-destructive">{fieldErrors.last}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="gnci-phone">Mobile phone<GnciReq /></label>
                    <input id="gnci-phone" type="tel" inputMode="tel" autoComplete="tel" required aria-required="true" aria-invalid={!!fieldErrors.phone} className={inputCls + (fieldErrors.phone ? " border-destructive ring-1 ring-destructive" : "")} value={phone} onChange={e => {
                setPhone(e.target.value);
                if (fieldErrors.phone) setFieldErrors({
                  ...fieldErrors,
                  phone: ""
                });
              }} />
                    {fieldErrors.phone && <p className="mt-1 text-sm text-destructive">{fieldErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" htmlFor="gnci-email">Email<GnciReq /></label>
                    <input id="gnci-email" type="email" inputMode="email" autoComplete="email" required aria-required="true" aria-invalid={!!fieldErrors.email} className={inputCls + (fieldErrors.email ? " border-destructive ring-1 ring-destructive" : "")} value={email} onChange={e => {
                setEmail(e.target.value);
                if (fieldErrors.email) setFieldErrors({
                  ...fieldErrors,
                  email: ""
                });
              }} />
                    {fieldErrors.email && <p className="mt-1 text-sm text-destructive">{fieldErrors.email}</p>}
                  </div>
                </div>
                <GnciAddressField value={address} onChange={a => {
            setAddress(a);
            if (fieldErrors.street || fieldErrors.city || fieldErrors.zip) setFieldErrors({
              ...fieldErrors,
              street: "",
              city: "",
              zip: ""
            });
          }} errors={fieldErrors} />
                <div>
                  <label className="block text-sm font-medium mb-1.5" htmlFor="gnci-notes">Anything we should know? (optional)</label>
                  <textarea id="gnci-notes" rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-base" value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="gnci-website">Website</label>
                  <input id="gnci-website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
                </div>
                <label className={"flex items-start gap-3 text-sm text-muted-foreground" + (fieldErrors.consent ? " rounded-md ring-1 ring-destructive p-2" : "")}>
                  <input id="gnci-consent" type="checkbox" required aria-required="true" className="mt-1 size-5 shrink-0 accent-[var(--primary)]" checked={consent} onChange={e => {
              setConsent(e.target.checked);
              if (fieldErrors.consent) setFieldErrors({
                ...fieldErrors,
                consent: ""
              });
            }} />
                  <span>I agree that Good Neighbor Construction may contact me by phone, text message and email about this inquiry and appointment. Message and data rates may apply. Reply STOP to opt out of texts. Consent is not a condition of purchase.</span>
                </label>
                {fieldErrors.consent && <p className="-mt-2 text-sm text-destructive">{fieldErrors.consent}</p>}
                {error && <p className="text-destructive font-medium" role="alert">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-primary px-8 h-12 text-base font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">
                  {submitting ? <><LoaderCircle className="size-5 animate-spin" /> Confirming your appointment...</> : type === "onsite" ? "Book My Free Onsite Consultation" : "Book My Intake Call"}
                </button>
                {submitting && <p className="text-sm text-muted-foreground" role="status">Checking the calendar and confirming your time. This can take up to 20 seconds, so please keep this page open.</p>}
                <p className="flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="size-4" /> Licensed, bonded and insured. CSLB #1134435. Your appointment is confirmed only after it is on our calendar.</p>
              </fieldset>}
            {type && !start && error && <p className="text-destructive font-medium" role="alert">{error}</p>}
          </form>}
      </div>
    </section>;
}
/* =================== end GNCI booking form (shared code) =================== */

export default function Finalcalltoaction() {
  const shouldReduceMotion = useReducedMotion();
  return <>
    <section id="financing" data-nav="light" className="bg-muted/30 text-foreground border-y border-border py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <span className="inline-flex items-center rounded-full border border-border px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary">Renovation Financing</span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight leading-tight">Financing options for your remodel</h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">Through our financing partner RenoFi, homeowners can explore renovation loans that may let you borrow based on your home's expected value after the project is complete. Checking your options will not impact your credit.</p>
              <ul className="space-y-2.5 text-sm md:text-base">
                <li className="flex items-start gap-2.5"><CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />Estimate how much you may be able to borrow on our RenoFi financing page</li>
                <li className="flex items-start gap-2.5"><CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />Available for home additions, as well as kitchens, bathrooms and ADUs</li>
                <li className="flex items-start gap-2.5"><CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />A RenoFi renovation advisor walks you through loan options and next steps</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button asChild size="lg" className="rounded-md font-semibold px-7">
                  <a href="https://pros.renofi.com/pros/good-neighbor-construction" target="_blank" rel="noopener noreferrer">Explore Financing with RenoFi</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-md font-semibold px-6">
                  <Link to="/#request-estimate">Start Your Project</Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-xl border border-border bg-card p-6 md:p-7 shadow-sm space-y-4">
                <h3 className="font-semibold text-lg">How it works</h3>
                <ol className="space-y-3 text-sm text-muted-foreground list-decimal pl-5">
                  <li>Explore rates and estimate your borrowing power on our RenoFi financing page, or choose "Yes, check my financing options" when you start your project.</li>
                  <li>A RenoFi renovation advisor reviews your information and contacts you about loan options.</li>
                  <li>We plan your project with a clear written scope so you can compare your financing with confidence.</li>
                </ol>
                <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-4">Financing is provided by RenoFi (NMLS ID 1802847) and its lending partners, not by Good Neighbor Construction. All loans are subject to credit approval, underwriting and lender terms. Not available in all states.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    
  </>;
}
export { GnciBooking };